// src/pages/RealEstatePage.js
import styled from "styled-components";
import Newsletter from "../components/Newsletter";
import Footer from "../components/Footer";
import { useLocation } from "react-router";
import { useEffect, useState } from "react";
import { publicRequest } from "../requestMethods";
import { useNavigate } from "react-router-dom";

import ArrowLeftOutlinedIcon from "@mui/icons-material/ArrowLeftOutlined";
import ArrowRightOutlinedIcon from "@mui/icons-material/ArrowRightOutlined";

/* ---------------- Slider Styles ---------------- */

const SliderContainer = styled.div`
  width: 100%;
  height: 500px;
  position: relative;
  overflow: hidden;
  border-radius: 12px;

  @media only screen and (max-width: 768px) {
    height: 280px;
    border-radius: 8px;
  }
`;

const Arrow = styled.div`
  width: 50px;
  height: 50px;
  background-color: #fff7f7;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${(props) => props.direction === "left" && "10px"};
  right: ${(props) => props.direction === "right" && "10px"};
  margin: auto;
  cursor: pointer;
  opacity: 0.6;
  z-index: 2;

  @media only screen and (max-width: 768px) {
    width: 36px;
    height: 36px;
  }
`;

const SliderWrapper = styled.div`
  height: 100%;
  display: flex;
  transition: all 1s ease;
  transform: translateX(-${(props) => props.slideIndex * 100}%);
`;

const Slide = styled.div`
  min-width: 100%;
  height: 100%;
  display: flex;
`;

const ImageContainer = styled.div`
  width: 100%;
  height: 100%;
`;

const EstateImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

/* ---------------- Page Layout ---------------- */

const Container = styled.div``;

const Wrapper = styled.div`
  padding: 50px;
  display: flex;
  gap: 40px;

  @media only screen and (max-width: 768px) {
    padding: 15px;
    flex-direction: column;
    gap: 20px;
  }
`;

const InfoContainer = styled.div`
  flex: 1;
  padding: 0px 50px;

  @media only screen and (max-width: 768px) {
    padding: 0;
  }
`;

const Title = styled.h1`
  font-weight: 300;
  margin-bottom: 20px;

  @media only screen and (max-width: 768px) {
    font-size: 22px;
    margin-bottom: 10px;
  }
`;

const Detail = styled.p`
  font-size: 20px;
  margin: 10px 0;
  color: #444;

  @media only screen and (max-width: 768px) {
    font-size: 15px;
    margin: 6px 0;
  }
`;

const Description = styled.p`
  margin: 20px 0;
  font-size: 18px;
  line-height: 1.5;
  color: #555;

  @media only screen and (max-width: 768px) {
    font-size: 14px;
    margin: 12px 0;
  }
`;

const Price = styled.h2`
  font-size: 40px;
  font-weight: 700;
  color: #0a7cff;
  margin-top: 20px;

  @media only screen and (max-width: 768px) {
    font-size: 26px;
    margin-top: 12px;
  }
`;

/* ---------------- Component ---------------- */

const RealEstatePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const id = location.pathname.split("/")[3]; // /realestate/item/:id

  const [item, setItem] = useState({});
  const [owner, setOwner] = useState(null);

  const [slideIndex, setSlideIndex] = useState(0);

  useEffect(() => {
    const getItem = async () => {
      try {
        const res = await publicRequest.get(
          "/AffordableRealestates/item/" + id
        );
        setItem(res.data);

        if (res.data.owner) {
          const ownerRes = await publicRequest.get(
            "/auth/user/" + res.data.owner
          );
          setOwner(ownerRes.data);
        }
      } catch (err) {
        console.log("Error loading real estate:", err);
      }
    };

    getItem();
  }, [id]);

  useEffect(() => {
    if (item?.type && item?.city && owner?.storeName) {
      document.title = `${item.type} in ${item.city} — ${owner.storeName}`;
    }
  }, [item, owner]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  /* Build image array: main image + secondary images */
  const images = [
    item.img && `/uploads/${item.img}`,
    item.img1 && `/uploads/${item.img1}`,
    item.img2 && `/uploads/${item.img2}`,
    item.img3 && `/uploads/${item.img3}`,
    item.img4 && `/uploads/${item.img4}`,
    item.img5 && `/uploads/${item.img5}`,
    item.img6 && `/uploads/${item.img6}`,
  ].filter(Boolean);

  const handleClick = (direction) => {
    if (direction === "left") {
      setSlideIndex((prev) => (prev - 1 + images.length) % images.length);
    } else {
      setSlideIndex((prev) => (prev + 1) % images.length);
    }
  };

  return (
    <Container>
      <Wrapper>
        {/* ----------- Image Slider ----------- */}
        <SliderContainer>
          <Arrow direction="left" onClick={() => handleClick("left")}>
            <ArrowLeftOutlinedIcon />
          </Arrow>

          <SliderWrapper slideIndex={slideIndex}>
            {images.map((src, index) => (
              <Slide key={index}>
                <ImageContainer>
                  <EstateImage src={src} alt={`estate-${index}`} />
                </ImageContainer>
              </Slide>
            ))}
          </SliderWrapper>

          <Arrow direction="right" onClick={() => handleClick("right")}>
            <ArrowRightOutlinedIcon />
          </Arrow>
        </SliderContainer>

        {/* ----------- Info Section ----------- */}
        <InfoContainer>
          <Title>{item.type}</Title>

          <Detail>City: {item.city}</Detail>

          <Detail
            style={{
              color: "#0a7cff",
              cursor: "pointer",
              textDecoration: "underline",
            }}
            onClick={() => navigate(`/store/${owner._id}?tab=realestate`)}
          >
            Store: {owner?.storeName}
          </Detail>

          <Detail>Mobile: {owner?.mobile}</Detail>

          <Description>{item.description}</Description>

          <Price>{item.price} SR</Price>
        </InfoContainer>
      </Wrapper>

      <Newsletter />
      <Footer />
    </Container>
  );
};

export default RealEstatePage;
